/**
 * Review Aggregation Agent
 * Aggregates reviews from multiple sources and updates listings
 */

import jinaAI from '../jinaAI';

interface ReviewSource {
  name: string;
  rating: number;
  reviewCount: number;
  url?: string;
}

interface AggregatedRating {
  averageRating: number;
  totalReviews: number;
  sources: ReviewSource[];
  lastUpdated: string;
  confidence: number; // 0-1 score based on data quality
}

class ReviewAggregationAgent {
  /**
   * Fetch and aggregate reviews for a business
   */
  async aggregateReviews(businessName: string, location: string, websiteUrl?: string): Promise<AggregatedRating> {
    try {
      const reviews = await jinaAI.getAggregatedReviews(businessName, location);

      const sources: ReviewSource[] = reviews.sources.map(source => ({
        name: source.source,
        rating: source.rating,
        reviewCount: source.reviewCount,
      }));

      // Calculate confidence based on number of sources and reviews
      const confidence = this.calculateConfidence(sources);

      return {
        averageRating: reviews.averageRating,
        totalReviews: reviews.totalReviews,
        sources,
        lastUpdated: new Date().toISOString(),
        confidence,
      };
    } catch (error) {
      console.error('Error aggregating reviews:', error);
      return this.getDefaultRating();
    }
  }

  /**
   * Update multiple listings with reviews
   */
  async batchUpdateReviews(listings: any[]): Promise<Map<string, AggregatedRating>> {
    const results = new Map<string, AggregatedRating>();

    for (const listing of listings) {
      try {
        const rating = await this.aggregateReviews(
          listing.name,
          `${listing.city}, ${listing.state}`
        );

        results.set(listing.id, rating);

        // Rate limiting - wait 2 seconds between requests
        await new Promise(resolve => setTimeout(resolve, 2000));
      } catch (error) {
        console.error(`Error updating reviews for ${listing.name}:`, error);
        results.set(listing.id, this.getDefaultRating());
      }
    }

    return results;
  }

  /**
   * Calculate confidence score based on data quality
   */
  private calculateConfidence(sources: ReviewSource[]): number {
    if (sources.length === 0) return 0;

    let score = 0;

    // Source diversity (0-0.4)
    score += Math.min(sources.length * 0.2, 0.4);

    // Review volume (0-0.4)
    const totalReviews = sources.reduce((sum, s) => sum + s.reviewCount, 0);
    if (totalReviews >= 100) score += 0.4;
    else if (totalReviews >= 50) score += 0.3;
    else if (totalReviews >= 20) score += 0.2;
    else if (totalReviews >= 10) score += 0.1;

    // Consistency across sources (0-0.2)
    const ratings = sources.map(s => s.rating);
    const variance = this.calculateVariance(ratings);
    if (variance < 0.5) score += 0.2;
    else if (variance < 1.0) score += 0.1;

    return Math.min(score, 1.0);
  }

  /**
   * Calculate variance of ratings
   */
  private calculateVariance(numbers: number[]): number {
    if (numbers.length === 0) return 0;

    const mean = numbers.reduce((sum, n) => sum + n, 0) / numbers.length;
    const squaredDiffs = numbers.map(n => Math.pow(n - mean, 2));
    return squaredDiffs.reduce((sum, d) => sum + d, 0) / numbers.length;
  }

  /**
   * Get default rating when scraping fails
   */
  private getDefaultRating(): AggregatedRating {
    return {
      averageRating: 0,
      totalReviews: 0,
      sources: [],
      lastUpdated: new Date().toISOString(),
      confidence: 0,
    };
  }

  /**
   * Normalize rating to 5-star scale
   */
  normalizeRating(rating: number, maxScale: number = 5): number {
    if (maxScale === 5) return rating;
    return (rating / maxScale) * 5;
  }

  /**
   * Get rating category
   */
  getRatingCategory(rating: number): string {
    if (rating >= 4.5) return 'Excellent';
    if (rating >= 4.0) return 'Very Good';
    if (rating >= 3.5) return 'Good';
    if (rating >= 3.0) return 'Average';
    if (rating >= 2.0) return 'Below Average';
    return 'Poor';
  }

  /**
   * Should update reviews (check if data is stale)
   */
  shouldUpdateReviews(lastUpdated: string, daysThreshold: number = 30): boolean {
    const lastUpdateDate = new Date(lastUpdated);
    const now = new Date();
    const daysSinceUpdate = (now.getTime() - lastUpdateDate.getTime()) / (1000 * 60 * 60 * 24);

    return daysSinceUpdate >= daysThreshold;
  }

  /**
   * Generate review summary
   */
  generateReviewSummary(rating: AggregatedRating): string {
    if (rating.totalReviews === 0) {
      return 'No reviews available yet. Be the first to review!';
    }

    const category = this.getRatingCategory(rating.averageRating);
    const sources = rating.sources.map(s => s.name).join(', ');

    return `Rated ${rating.averageRating.toFixed(1)} out of 5 stars (${category}) based on ${rating.totalReviews} reviews from ${sources}.`;
  }
}

export default new ReviewAggregationAgent();
